import { useRouter } from 'next/navigation';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MarketplaceCategoryBlock } from 'src/components/MarketplaceCategoryBlock';
import { Filters, MobileFilters } from 'src/components/MarketplaceFilters';
import { MarketplaceHero } from 'src/components/MarketplaceHero';
import { MarketplaceNotFoundBlock } from 'src/components/MarketplaceNotFoundBlock';
import { MarketplaceShowcaseBlock } from 'src/components/MarketplaceShowcaseBlock';
import { Search } from 'src/components/Search';
import { Wrapper } from 'src/components/Wrapper';
import { useGtmDataLayer } from 'src/hooks/useGtmDataLayer';
import { useWindowWidth } from 'src/hooks/useWindowWidth';
import { MarketplaceProps, ParsedProductPreview } from 'src/types/marketplace';
import { Button } from 'src/uikit/Button';
import { Filter as OpenFilterIcon } from 'src/uikit/icons/ContentIcons/TechIcons/General/Filter';
import { getCategoryBreadcrumbs } from 'src/utils/marketplace/category';
import {
  getFilteredCards,
  getFiltersForCards,
  getSelectedFiltersNames,
  isFiltered,
} from 'src/utils/marketplace/filter';
import {
  getMarketplaceCategoryUrl,
  SITE_MARKETPLACE_URL,
} from 'src/utils/marketplace/paths';

import { arrayChuck } from '../../utils/arrayChuck';
import { MarketplaceMoreSolutionsBanner } from '../MarketplaceMoreSolutionsBanner';
import s from './MarketplacePage.module.scss';

const MocksRu = {
  title: 'Маркетплейс',
  description:
    'Инструменты для разработки и анализа данных, обеспечения безопасности, создания бизнес-приложений и решения других задач от ведущих вендоров.',
  popular: 'Популярное',
  services: 'Сервисов',
  filters: 'Фильтры',
};

const BANNER_IMAGE_URL =
  'https://cdn.cloud.ru/backend/marketplace/marketplace_banner.png';
const SCROLL_TO_BLOCK_ID = 'scrollIntoBlock';

function OpenFilterButton({ onOpen }: { onOpen: () => void }) {
  return (
    <Button
      onClick={onOpen}
      className={s.openFilters}
      variant={Button.variants.Outline}
      analyticsOptions={{
        action: 'click',
        clickZone: 'body',
        clickElement: 'button',
        clickContent: MocksRu.filters,
        uniqueId: 'marketplace-open-mobile-filter-button',
        transitionType: 'inside-link',
      }}
    >
      <OpenFilterIcon />
      <span>{MocksRu.filters}</span>
    </Button>
  );
}

function MarketplaceContent({
  cards = [],
  categories = [],
  currentCategory,
}: MarketplaceProps) {
  const router = useRouter();

  const [categoriesBeforeBanner, categoriesAfterBanner] = arrayChuck({
    array: categories,
    // TODO make extensible
    chuckSize: 3,
  });

  const [mobileFilterVisible, setMobileFilterVisible] = useState(false);
  const [cardsInCategory, setCardsInCategory] =
    useState<ParsedProductPreview[]>(cards);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setCardsInCategory(cards);
  }, [cards, currentCategory]);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {},
  });

  const {
    windowWidth,
    breakpoints: { MD },
  } = useWindowWidth();

  const filtersToApply = methods.watch();

  const { sendDataToDataLayer } = useGtmDataLayer();

  const selectedCategory = categories.find(
    (category) => category.slug === currentCategory,
  );
  const availableFilters = useMemo(
    () =>
      (selectedCategory
        ? selectedCategory.availableFilters
        : getFiltersForCards(cards)) || [],
    [cards, selectedCategory],
  );

  const handleSearch = (search: string) => {
    setSearchValue(search);

    const dataLayerToSend = {
      dataLayer: {
        event: 'custom_event',
        event_category: 'marketplace',
        event_action: 'marketplace_search',
        event_label: search,
      },
    };

    sendDataToDataLayer(dataLayerToSend);

    const searchString = search.toLowerCase();

    availableFilters.forEach(({ filterKey }) =>
      // @ts-ignore
      methods.resetField(filterKey, { defaultValue: [] }),
    );

    setCardsInCategory(
      cards.filter(
        (c) =>
          c.displayName.toLowerCase().includes(searchString) ||
          c.previewText.toLowerCase().includes(searchString),
      ),
    );
  };

  useEffect(() => {
    if (isFiltered(filtersToApply)) {
      setSearchValue('');

      const dataLayerToSend = {
        dataLayer: {
          event: 'custom_event',
          event_category: 'marketplace',
          event_action: `filter_products_${currentCategory}`,
          event_label: getSelectedFiltersNames(
            filtersToApply,
            availableFilters,
          ),
        },
      };
      sendDataToDataLayer(dataLayerToSend);
    }
  }, [
    cards,
    currentCategory,
    availableFilters,
    filtersToApply,
    sendDataToDataLayer,
  ]);

  const handleCategoryChange = (newCategory: string) => {
    const dataLayerToSend = {
      dataLayer: {
        event: 'custom_event',
        event_category: 'marketplace',
        event_action: `click_category_${newCategory}`,
        event_label: newCategory,
      },
    };

    sendDataToDataLayer(dataLayerToSend);
    setSearchValue('');

    router.push(
      currentCategory === newCategory
        ? SITE_MARKETPLACE_URL
        : getMarketplaceCategoryUrl(newCategory),
    );
  };

  const popularProducts = cards.filter((card) => card.popular);
  const isSingleBlock = Boolean(
    selectedCategory || searchValue.trim() || isFiltered(filtersToApply),
  );

  const filteredProducts = useMemo(
    () =>
      isFiltered(filtersToApply)
        ? getFilteredCards(cards, filtersToApply)
        : cardsInCategory,
    [cards, cardsInCategory, filtersToApply],
  );

  return (
    <>
      <div className={s.searchBlock}>
        <div id={SCROLL_TO_BLOCK_ID} className={s.searchWrapper}>
          <Search value={searchValue} setValue={handleSearch} />
        </div>

        {windowWidth && windowWidth <= MD ? (
          <OpenFilterButton onOpen={() => setMobileFilterVisible(true)} />
        ) : null}
      </div>

      <FormProvider {...methods}>
        {cardsInCategory.length ? (
          <section className={s.main}>
            <div className={s.showcaseWrapper}>
              {isSingleBlock ? (
                <>
                  <MarketplaceShowcaseBlock
                    cards={filteredProducts}
                    counter={filteredProducts.length}
                    title={
                      selectedCategory
                        ? selectedCategory.displayName
                        : MocksRu.services
                    }
                    description={
                      selectedCategory ? selectedCategory.description : ''
                    }
                  />
                  <MarketplaceMoreSolutionsBanner
                    isBannerAtTheEnd={isSingleBlock}
                  />
                </>
              ) : (
                <>
                  {popularProducts && popularProducts.length > 0 && (
                    <MarketplaceShowcaseBlock
                      isPopular
                      cards={popularProducts}
                      title={MocksRu.popular}
                    />
                  )}
                  {categoriesBeforeBanner.map((category) => (
                    <MarketplaceShowcaseBlock
                      key={category.type}
                      cards={category.cards.slice(0, 3)}
                      onCategoryChange={() =>
                        handleCategoryChange(category.slug)
                      }
                      title={category.displayName}
                      description={category.description}
                      counter={category.cards.length}
                    />
                  ))}
                  <MarketplaceMoreSolutionsBanner />
                  {categoriesAfterBanner?.map((category) => (
                    <MarketplaceShowcaseBlock
                      key={category.type}
                      cards={category.cards.slice(0, 3)}
                      onCategoryChange={() =>
                        handleCategoryChange(category.slug)
                      }
                      title={category.displayName}
                      description={category.description}
                      counter={category.cards.length}
                    />
                  ))}
                </>
              )}
            </div>

            <div className={s.sidebar}>
              {windowWidth && windowWidth <= MD ? null : (
                <MarketplaceCategoryBlock
                  onChange={handleCategoryChange}
                  categories={categories}
                  currentCategory={currentCategory}
                />
              )}

              <Filters availableFilters={availableFilters || []} />
            </div>
          </section>
        ) : (
          <MarketplaceNotFoundBlock />
        )}

        {windowWidth && windowWidth <= MD && mobileFilterVisible ? (
          <MobileFilters
            availableFilters={availableFilters}
            setVisible={setMobileFilterVisible}
            onChange={handleCategoryChange}
            categories={categories}
            currentCategory={currentCategory}
          />
        ) : null}
      </FormProvider>
    </>
  );
}

export function MarketplacePage({
  cards = [],
  categories = [],
  currentCategory,
}: MarketplaceProps) {
  const category = categories.find(
    (category) => category.slug === currentCategory,
  );

  return (
    <>
      <div className={s.root}>
        <MarketplaceHero
          breadcrumbs={getCategoryBreadcrumbs(category?.displayName)}
          title={MocksRu.title}
          description={MocksRu.description}
          image={BANNER_IMAGE_URL}
        />

        <Wrapper>
          <MarketplaceContent
            cards={cards}
            categories={categories}
            currentCategory={currentCategory}
          />
        </Wrapper>
      </div>
    </>
  );
}
